import { ExpandMoreRounded } from "@mui/icons-material";
import {
  Accordion,
  AccordionSummary,
  Box,
  Card,
  CardHeader,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ITreeItem } from "src/models";
import { getShortLabel } from "../..";
import { ISurveyInstancePickerOption, SurveyInstancePicker } from "../../../components";
import { configuration } from "../../../configuration";
import { useGetSurveyCoherenceReport, useGetTeam, useGetTeamsTree } from "../../../http";
import {
  CoherenceScore,
  CoherenceTeamPicker,
  LinearScaleResults,
  TextResponses,
} from "../../ReportsPages/CoherenceReportPage/components";

interface IProps {
  teamSlug: string;
}

const TeamCoherenceReview = (props: IProps) => {
  const theme = useTheme();
  const { data: team, isLoading: isLoadingTeam } = useGetTeam(props.teamSlug);
  const [selectedSurveyInstance, setSelectedSurveyInstance] = useState<ISurveyInstancePickerOption | undefined>(
    undefined
  );
  const { data: teams, isLoading: isLoadingTeams } = useGetTeamsTree(
    selectedSurveyInstance?.startDateTimeUtc,
    selectedSurveyInstance?.endDateTimeUtc
  );
  const [selectedTeamGuids, setSelectedTeamGuids] = useState<string[] | undefined>(undefined);
  const [teamShortLabel, setTeamShortLabel] = useState<string | undefined>(undefined);
  const { data: report, isLoading } = useGetSurveyCoherenceReport(selectedSurveyInstance?.guid, selectedTeamGuids);

  useEffect(() => {
    if (!isLoadingTeam && team) {
      setSelectedTeamGuids([team.guid]);
      setTeamShortLabel(team.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingTeam]);

  function handleTeamChanged(teams: ITreeItem[]) {
    setSelectedTeamGuids(teams.map((x) => x.itemId));
    setTeamShortLabel(getShortLabel(teams) ?? "Select Team");
  }

  return (
    <Stack spacing={1}>
      <Card>
        <CardHeader title={<Typography variant="h6">Coherence Review</Typography>} />
        <Divider />
        <Stack direction="row" spacing={1} sx={{ p: 0.5 }}>
          <SurveyInstancePicker
            surveyGuid={configuration.coherenceSurveyGuid}
            surveyInstanceGuid={selectedSurveyInstance?.guid}
            onChange={(value) => setSelectedSurveyInstance(value)}
            hideActiveInstance
          />
          <CoherenceTeamPicker
            isLoading={isLoadingTeams}
            teams={teams ?? []}
            selectedTeamGuids={selectedTeamGuids ?? []}
            isMeVisible={false}
            onTeamChanged={handleTeamChanged}
            onTeamCleared={() => handleTeamChanged([])}
          />
        </Stack>
        <Divider />
        <Box sx={{ p: 1 }}>
          <CoherenceScore
            color={theme.palette.primary.main}
            teamLabel={teamShortLabel ?? "-"}
            isLoading={isLoading}
            coherenceSummary={selectedTeamGuids?.length ? report?.selectedTeamsCoherenceSummary : undefined}
          />
        </Box>
      </Card>
      <Card>
        <Accordion disableGutters sx={{ backgroundColor: "transparent" }}>
          <AccordionSummary expandIcon={<ExpandMoreRounded fontSize="large" />}>
            <Typography variant="h5">Rating Responses</Typography>
          </AccordionSummary>
          <Divider sx={{ color: "#c9c9c9" }} />
          <LinearScaleResults
            isLoading={isLoading}
            report={report}
            selectedLabel={`${teamShortLabel ?? "-"}, ${selectedSurveyInstance?.name}`}
          />
        </Accordion>
      </Card>
      <Card>
        <Accordion disableGutters sx={{ backgroundColor: "transparent" }}>
          <AccordionSummary expandIcon={<ExpandMoreRounded fontSize="large" />}>
            <Typography variant="h5">Text Responses</Typography>
          </AccordionSummary>
          <Divider sx={{ color: "#c9c9c9" }} />
          <TextResponses isLoading={isLoading} report={report} />
        </Accordion>
      </Card>
    </Stack>
  );
};

export { TeamCoherenceReview };
